import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import { heroBannerToItem, isBannerVisible, buildDefaultHeroBanner } from '@/lib/heroBanners';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';
import { normalizeCurrency } from '@/lib/currency';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { getOpenState, hasOpeningHours } from '@/lib/restaurantHours';
import AnnonceCard from '@/components/feature/marketplace/AnnonceCard';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import {
  fetchMarketplaceSellerIds,
  fetchMarketplaceCategories,
  fetchMarketplaceAnnonces,
  fetchMarketplaceAdBanners,
  fetchSellersByIds,
  fetchFeaturedSellers,
  fetchSellerNetworks,
  fetchVendorCategoryLabels,
  type MarketplaceAnnonce,
  type MarketplaceCategory,
  type MarketplaceSeller,
  type MarketplaceAdBanner,
  type VendorCategoryLabels,
} from '@/lib/marketplaceCatalog';
import type { Reseau } from '@/lib/networks';

// ─────────────────────────────────────────────────────────────
// Page d'accueil publique — type de compte « Marketplace mixte
// produits et services » (cas 5). Agrège le contenu publié par les
// vendeurs de la marketplace.
// Blocs : Bannière → Catégories populaires → Annonces récentes →
//         Produits des vendeurs → Pub → Vendeurs en avant → Pub →
//         Services des vendeurs
// ─────────────────────────────────────────────────────────────

const ANNOUNCES_LIMIT = 6; // 3 colonnes × 2 lignes (identique PC / mobile)
const CATALOG_LIMIT = 9; // 3 colonnes × 3 lignes (identique PC / mobile)
const SELLERS_LIMIT = 6;

const SELLER_SOCIALS: { key: keyof MarketplaceSeller; icon: string; label: string }[] = [
  { key: 'lienfacebook', icon: 'ri-facebook-circle-line', label: 'Facebook' },
  { key: 'lieninstagram', icon: 'ri-instagram-line', label: 'Instagram' },
  { key: 'lientiktok', icon: 'ri-tiktok-line', label: 'TikTok' },
  { key: 'lientwitter', icon: 'ri-twitter-x-line', label: 'X' },
  { key: 'lienlinkedin', icon: 'ri-linkedin-box-line', label: 'LinkedIn' },
  { key: 'lienyoutube', icon: 'ri-youtube-line', label: 'YouTube' },
];

function SectionHeading({
  icon,
  title,
  linkTo,
  linkLabel = 'Voir plus',
}: {
  icon: string;
  title: string;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="flex items-end justify-between mb-6 gap-3">
      <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 flex items-center">
        <i className={`${icon} mr-2 text-primary-500`}></i>
        {title}
      </h2>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap"
        >
          {linkLabel} <i className="ri-arrow-right-line ml-1"></i>
        </Link>
      )}
    </div>
  );
}

function AdBannerBlock({ banners }: { banners: MarketplaceAdBanner[] }) {
  if (banners.length === 0) return null;
  return (
    <section className="py-8 md:py-12 bg-background-100/50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-5">
          <i className="ri-advertisement-line text-primary-500"></i>
          <h2 className="text-sm font-bold uppercase tracking-wider text-foreground-600">Recommandation</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {banners.slice(0, 2).map((b) => {
            const img = resolveMediaUrl(b.image);
            return (
              <div
                key={b.id}
                className="relative rounded-xl overflow-hidden border border-background-200/70 bg-background-50 h-40 md:h-52"
              >
                {img ? (
                  <img src={img} alt={b.titre} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-background-100">
                    <i className="ri-image-line text-3xl text-foreground-300"></i>
                  </div>
                )}
                {(b.titre || b.description) && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent flex flex-col justify-end p-4">
                    {b.titre && <h3 className="text-base md:text-lg font-bold text-white">{b.titre}</h3>}
                    {b.description && <p className="text-xs text-white/80 line-clamp-1">{b.description}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function MarketplaceHome() {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const { banners } = useHeroBanners(merchantId);

  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [annonces, setAnnonces] = useState<MarketplaceAnnonce[]>([]);
  const [produits, setProduits] = useState<MarketplaceAnnonce[]>([]);
  const [services, setServices] = useState<MarketplaceAnnonce[]>([]);
  const [sellers, setSellers] = useState<MarketplaceSeller[]>([]);
  const [adBanners, setAdBanners] = useState<MarketplaceAdBanner[]>([]);
  const [networks, setNetworks] = useState<Reseau[]>([]);
  const [sellerNames, setSellerNames] = useState<Record<number, string>>({});
  const [sellerRatings, setSellerRatings] = useState<Record<number, VendorRating>>({});
  const [catLabels, setCatLabels] = useState<Record<number, VendorCategoryLabels>>({});
  const [loading, setLoading] = useState(true);

  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const storeName = resolveStoreName(tenant, theme);
  const storeDescription = htmlToText(tenant?.description || tenant?.aboutus);
  // Badge « Ouvert / Fermé » de la marketplace elle-même (ses propres horaires).
  const marketOpen = hasOpeningHours(tenant?.horaires_json) ? getOpenState(tenant?.horaires_json) : null;

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const sellerIds = await fetchMarketplaceSellerIds(merchantId);
        const [
          catList,
          annonceList,
          produitList,
          serviceList,
          sellerBase,
          ads,
        ] = await Promise.all([
          fetchMarketplaceCategories(merchantId),
          fetchMarketplaceAnnonces(merchantId, { kind: 'autres', limit: ANNOUNCES_LIMIT, sellerIds }),
          fetchMarketplaceAnnonces(merchantId, { kind: 'produits', limit: CATALOG_LIMIT, sellerIds }),
          fetchMarketplaceAnnonces(merchantId, { kind: 'services', limit: CATALOG_LIMIT, sellerIds }),
          fetchSellersByIds(sellerIds),
          fetchMarketplaceAdBanners(merchantId, 2),
        ]);
        if (cancelled) return;
        setCategories(catList.filter((c) => !c.parent_id));
        setAnnonces(annonceList);
        setProduits(produitList);
        setServices(serviceList);
        setAdBanners(ads);

        // Nom de la boutique pour chaque carte de contenu.
        const nameMap: Record<number, string> = {};
        sellerBase.forEach((s) => { nameMap[s.userId] = s.nomcommerce; });
        setSellerNames(nameMap);

        // Notes moyennes des vendeurs (classement visible sur les cartes).
        const rated = await fetchVendorRatings(sellerIds);
        if (cancelled) return;
        setSellerRatings(rated);
        const labels = await fetchVendorCategoryLabels(merchantId, sellerIds);
        if (cancelled) return;
        setCatLabels(labels);

        const nets = await fetchSellerNetworks(merchantId);
        if (cancelled) return;
        setNetworks(nets);

        // Vendeurs mis en avant par la marketplace ; à défaut, tous les vendeurs.
        const featured = await fetchFeaturedSellers(merchantId);
        if (cancelled) return;
        setSellers((featured.length > 0 ? featured : sellerBase).slice(0, SELLERS_LIMIT));
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  const heroItems = banners.filter(isBannerVisible).map(heroBannerToItem);
  const defaultHero = heroItems.length === 0
    ? buildDefaultHeroBanner({ image: tenant?.imageaboutus, title: storeName, description: storeDescription })
    : null;
  const heroDisplay = defaultHero ? [defaultHero] : heroItems;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  const blocks: ReactNode[] = [];

  // 1. Bannière
  blocks.push(
    heroDisplay.length > 0 ? (
      <HeroBannerSlider key="banner" banners={heroDisplay} />
    ) : (
      <section key="banner" className="relative py-14 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-3">{storeName}</h1>
          {storeDescription && (
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {storeDescription}
            </p>
          )}
        </div>
      </section>
    ),
  );

  // 1b. Badge « membre du réseau » (si la marketplace a rejoint un réseau)
  if (networks.length > 0 || marketOpen) {
    blocks.push(
      <section key="network" className="border-b border-background-200/70 bg-accent-50/40">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-4 flex flex-wrap items-center justify-center gap-2">
          {marketOpen && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${marketOpen.open ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${marketOpen.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {marketOpen.open ? 'Ouvert maintenant' : 'Fermé maintenant'}
              {marketOpen.todayLabel && (
                <span className="font-normal opacity-70">· {marketOpen.todayLabel}</span>
              )}
            </span>
          )}
          {networks.map((n) => (
            <span
              key={n.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50"
              title={n.description || undefined}
            >
              <i className="ri-shield-check-line"></i>
              Membre du réseau {n.titre}
            </span>
          ))}
        </div>
      </section>,
    );
  }

  // 2. Catégories populaires
  if (categories.length > 0) {
    blocks.push(
      <section key="categories" className="py-10 md:py-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <SectionHeading icon="ri-apps-2-line" title="Catégories populaires" linkTo="/categories" />
          {/* Catégories : petits cercles compacts, comme des pastilles. */}
          <div className="flex flex-wrap items-start justify-center md:justify-start gap-x-3 gap-y-4 md:gap-x-6 md:gap-y-5">
            {categories.slice(0, 14).map((cat) => {
              const img = resolveMediaUrl(cat.image_couverture);
              return (
                <Link
                  key={cat.id}
                  to={`/marketplace/categorie/${cat.id}`}
                  className="group w-[64px] md:w-[84px] flex flex-col items-center gap-1.5 no-underline"
                >
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden border border-background-200/70 bg-background-100 flex items-center justify-center group-hover:border-primary-300 transition-colors">
                    {img ? (
                      <img
                        src={img}
                        alt={cat.nom}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <i className={`${cat.icon || 'ri-folder-line'} text-base md:text-lg text-foreground-400`}></i>
                    )}
                  </div>
                  <span className="text-[10px] md:text-[11px] font-medium text-foreground-700 capitalize text-center line-clamp-2 leading-tight">
                    {cat.nom}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>,
    );
  }

  // 3. Annonces récentes (3×2 PC / 2×2 mobile)
  if (annonces.length > 0) {
    blocks.push(
      <section key="annonces" className="py-10 md:py-14 bg-background-100/50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <SectionHeading icon="ri-megaphone-line" title="Annonces récentes" linkTo="/annonces" />
          {/* Toujours 3 cartes par ligne, sur mobile comme sur ordinateur. */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {annonces.map((a) => (
              <AnnonceCard
                key={a.id}
                annonce={a}
                monaie={monaie}
                sellerName={a.idvendeur ? sellerNames[a.idvendeur] : undefined}
                sellerRating={a.idvendeur ? sellerRatings[a.idvendeur] : undefined}
              />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/annonces"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Voir plus d&apos;annonces
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>,
    );
  }

  // 4. Produits des vendeurs (3×3)
  if (produits.length > 0) {
    blocks.push(
      <section key="produits" className="py-10 md:py-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <SectionHeading icon="ri-shopping-bag-3-line" title="Produits des vendeurs" linkTo="/marketplace/produits" />
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {produits.map((a) => (
              <AnnonceCard key={a.id} annonce={a} monaie={monaie} sellerName={a.idvendeur ? sellerNames[a.idvendeur] : undefined} sellerRating={a.idvendeur ? sellerRatings[a.idvendeur] : undefined} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/marketplace/produits"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Voir plus de produits
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>,
    );
  }

  // 5. Pub (Recommandation)
  blocks.push(<AdBannerBlock key="ads-1" banners={adBanners} />);

  // 6. Vendeurs mis en avant (Recommandation)
  if (sellers.length > 0) {
    blocks.push(
      <section key="sellers" className="py-10 md:py-14 bg-background-100/50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <SectionHeading icon="ri-user-star-line" title="Vendeurs mis en avant" linkTo="/vendeurs" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {sellers.map((s) => {
              const img = resolveMediaUrl(s.image);
              const open = hasOpeningHours(s.horairesJson) ? getOpenState(s.horairesJson) : null;
              const socials = SELLER_SOCIALS
                .map((net) => ({ ...net, url: String(s[net.key] || '').trim() }))
                .filter((net) => /^https?:\/\//i.test(net.url));
              return (
                <Link
                  key={s.userId}
                  to={`/boutique/${s.userId}`}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-4 hover:border-background-300/60 transition-colors no-underline"
                >
                  <div className="w-14 h-14 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {img ? (
                      <img src={img} alt={s.nomcommerce} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-lg font-bold text-foreground-400">
                        {s.nomcommerce.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground-900 truncate">{s.nomcommerce}</h3>
                      {open && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${open.open ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${open.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          {open.open ? 'Ouvert' : 'Fermé'}
                        </span>
                      )}
                    </div>
                    {s.ville && (
                      <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {s.ville}{s.pays ? `, ${s.pays}` : ''}
                      </span>
                    )}
                    {open && open.todayLabel && (
                      <span className="text-[11px] text-foreground-400 flex items-center gap-1 mt-0.5">
                        <i className="ri-time-line text-xs"></i>
                        {open.todayLabel === 'Fermé' ? "Fermé aujourd'hui" : `Aujourd'hui : ${open.todayLabel}`}
                      </span>
                    )}
                    {s.description && (
                      <p className="text-xs text-foreground-500 line-clamp-1 mt-1">{s.description}</p>
                    )}
                    <div className="mt-1">
                      <VendorRatingBadge
                        average={sellerRatings[s.userId]?.average ?? 0}
                        count={sellerRatings[s.userId]?.count ?? 0}
                      />
                    </div>
                    {catLabels[s.userId]?.souscategorieNom && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-semibold">
                        <i className="ri-price-tag-2-line text-[9px]"></i>
                        {catLabels[s.userId].souscategorieNom}
                      </span>
                    )}
                    {socials.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2">
                        {socials.map((net) => (
                          <span key={net.key as string} title={net.label} className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                            <i className={`${net.icon} text-xs`}></i>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {s.isFeatured && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-500 text-background-50 flex-shrink-0 self-start">
                      <i className="ri-star-fill"></i>
                      En avant
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>,
    );
  }

  // 7. Pub (Recommandation)
  blocks.push(<AdBannerBlock key="ads-2" banners={adBanners} />);

  // 8. Services des vendeurs (3×3)
  if (services.length > 0) {
    blocks.push(
      <section key="services" className="py-10 md:py-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <SectionHeading icon="ri-service-line" title="Services des vendeurs" linkTo="/marketplace/services" />
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {services.map((a) => (
              <AnnonceCard key={a.id} annonce={a} monaie={monaie} sellerName={a.idvendeur ? sellerNames[a.idvendeur] : undefined} sellerRating={a.idvendeur ? sellerRatings[a.idvendeur] : undefined} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/marketplace/services"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Voir plus de services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>,
    );
  }

  return <div className="min-h-screen bg-background-50">{blocks}</div>;
}